import { HandlingFee, IDiscounts, IOrderRow, IProduct, SurfaceTreatmentFee } from "@iloq/shared";

export const roundToCents = (n: number, isDiscountPrice = false): number => {
  // In discount price cases BC rounds half cents down so let's do so also here.
  // That's easiest achieved by utilizing the fact that Math.round() rounds negative half cases towards positive.
  const factor = isDiscountPrice ? -1 : 1;
  return (factor * Math.round(factor * n * 100)) / 100;
};

const calculateCartSum = (
  cartRows: IOrderRow[],
  discounts?: IDiscounts,
  serviceproductIds?: string[],
) => {
  let currencyCode = "";
  cartRows.forEach((row: IOrderRow) => {
    if (row.quantity > 0) {
      const ignoreDiscount =
        row.product.id === HandlingFee.sku ||
        row.product.id === SurfaceTreatmentFee.sku ||
        serviceproductIds?.includes(row.product.id);
      row.rowTotalSum = calculateRowTotalSum(row, discounts, ignoreDiscount);
      currencyCode = row.product.price!.currencyCode;
    } else {
      row.rowTotalSum = 0;
    }
  });
  const totalSum: number = cartRows.reduce(
    (accumulator, currentValue) => accumulator + currentValue.rowTotalSum,
    0,
  );

  return {
    totalSum: {
      price: roundToCents(totalSum),
      currencyCode,
    },
    rows: cartRows,
  };
};

export const calculateRowTotalSum = (
  row: IOrderRow,
  discounts?: IDiscounts,
  ignoreDiscount?: boolean,
) => {
  return calculateDiscountPrice(row, ignoreDiscount ? undefined : discounts) || 0;
};

export const calculateDiscountPrice = (
  row: IOrderRow,
  discounts?: IDiscounts,
  fallbackToFullPrice = true,
  calculateForSingleProduct = false,
) => {
  const quantity = calculateForSingleProduct ? 1 : row.quantity;
  if (discounts) {
    if (discounts.percentagePointDiscount) {
      const discountPercentage =
        (row.product.partnerDiscountPercentage || 0) + discounts.percentagePointDiscount;
      return roundToCents(
        quantity * row.product.price!.price * (1 - discountPercentage / 100),
        true,
      );
    }
    const netPrice = (discounts.netPrices || []).find((item) => item.sku === row.product.id)?.price;
    if (netPrice !== undefined) {
      return roundToCents(quantity * netPrice);
    } else if (discounts.onlineDiscount && isOnlineProduct(row.product)) {
      return roundToCents(
        quantity * row.product.price!.price * (1 - discounts.onlineDiscount / 100),
        true,
      );
    } else if (discounts.discount) {
      return roundToCents(
        quantity * row.product.price!.price * (1 - discounts.discount / 100),
        true,
      );
    }
  }

  const unitPrice = row.product.partnerDiscountPrice
    ? row.product.partnerDiscountPrice.price
    : fallbackToFullPrice
      ? row.product.price!.price
      : undefined;
  return unitPrice === undefined ? undefined : roundToCents(quantity * unitPrice);
};

const isOnlineProduct = (product: IProduct): boolean => product.main_category === "N";

export default calculateCartSum;
